import { createSelector } from "reselect";

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line import/no-cycle
import { ApplicationsDesignationUrl } from "routes/settings/integrations/constants";
import { isDefaultIntegration } from "routes/settings/integrations/helpers";
import { ICustomSectionDefinition, isValidSectionDefinition } from "services/custom-section";
import { IWithAgentsSessionState, selectIsAdmin } from "store/entities/agents";
import {
  IWithRequestsState,
  createHasFetchedSelector,
  createRequestErrorSelector,
  createRequestFetchingSelector,
} from "store/requests/selectors";

import { RequestAction } from "../actions";

import { ApplicationsActionNames } from "./actions";
import { ApplicationWidgetPlacement, IApplication, IApplicationWidget, IWidget } from "./interfaces";
import { IApplicationsState } from "./reducer";

export interface IWithApplicationsState {
  entities: {
    applications: IApplicationsState;
  };
}

interface IAppsMap {
  [key: string]: IApplication;
}

export const installedAppsSelector = (state: IWithApplicationsState): IAppsMap => state.entities.applications.installed;
export const privateAppsSelector = (state: IWithApplicationsState): IAppsMap => state.entities.applications.private;
export const inReviewAppsSelector = (state: IWithApplicationsState): IAppsMap => state.entities.applications.inReview;

const getApplications = (appsMap: IAppsMap = {}): IApplication[] => Object.keys(appsMap).map(id => appsMap[id]);

/*
 - only published apps could be visible on installed apps list
 - Services are using the same data model as applications, so they are on installed apps list but they don't have settings
 - hasSettingsHidden is custom props that gives us possibility to hide some apps from the settings view
*/
// @ts-expect-error TS(2322): Type 'string | boolean | undefined' is not assigna... Remove this comment to see the full error message
const filterInstalledApps = (app: IApplication): boolean => app.publishedAt && !app.isService && !app.hasSettingsHidden;

export const getInstalledApplications: (state: IWithApplicationsState) => IApplication[] = createSelector(
  installedAppsSelector,
  getApplications
);

export const getPrivateApplications: (state: IWithApplicationsState) => IApplication[] = createSelector(
  privateAppsSelector,
  getApplications
);

export const getInReviewApplications: (state: IWithApplicationsState) => IApplication[] = createSelector(
  inReviewAppsSelector,
  getApplications
);

export const getFilteredInstalledApplications: (state: IWithApplicationsState) => IApplication[] = createSelector(
  getInstalledApplications,
  (installedApps: IApplication[]) => installedApps.filter(filterInstalledApps)
);

const getWidgets = (appsMap: IAppsMap): IWidget[] =>
  // @ts-expect-error TS(2322): Type '{ name: string; appId: string; icon: string ... Remove this comment to see the full error message
  getApplications(appsMap)
    .map(app =>
      app.elements.widgets.map(w => ({
        ...w,
        name: app.name,
        appId: app.id,
        icon: app.icons && app.icons.small,
        path: `/apps/${w.appSlug || app.id}`,
      }))
    )
    .reduce((acc, widgets) => acc.concat(widgets), []);

export const getWidgetsWithPlacement = createSelector(
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any
  [installedAppsSelector, (_: any, placement: string) => placement],
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (appsMap: any, placement: any) => getWidgets(appsMap).filter(w => w.placement === placement)
);

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getFullscreenWidgets = createSelector([installedAppsSelector], (apps: any) =>
  getWidgets(apps).filter(({ placement }) => placement === ApplicationWidgetPlacement.FullScreen)
);

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getCustomerDetailsSections = createSelector([installedAppsSelector], (appsMap: any) =>
  getWidgets(appsMap)
    .filter(w => w.shortcut && w.shortcut.initialState)
    .map(w => {
      // @ts-expect-error TS(2339): Property 'initialState' does not exist on type '{ ... Remove this comment to see the full error message
      let { initialState } = w.shortcut;

      // The API returns the initial state as a stringified JSON string
      // (e.g. `"{\"customSections\": [ { \"title\": \"Example section\", \"components\": [] } ] }"`),
      // so we need to parse it two times.
      // @ts-expect-error TS(2532): Object is possibly 'undefined'.
      if (/^"(.*)"$/.test(w.shortcut.initialState)) {
        initialState = JSON.parse(initialState);
      }

      try {
        return {
          widget: w,
          initialState: JSON.parse(initialState),
        };
      } catch {
        return null;
      }
    })
    .filter(Boolean)
    .filter(
      // @ts-expect-error TS(2531): Object is possibly 'null'.
      w => w.initialState && typeof w.initialState === "object" && Array.isArray(w.initialState.customerDetailsSections)
    )
    .reduce((acc, w) => {
      // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
      acc[w.widget.id] = w.initialState.customerDetailsSections.reduce(
        // FIXME: Fix this the next time the file is edited.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (sections: any, section: ICustomSectionDefinition) =>
          isValidSectionDefinition(section)
            ? {
                ...sections,
                [section.title]: {
                  // @ts-expect-error TS(2531): Object is possibly 'null'.
                  name: createCustomSectionName(w.widget.id, section.title),
                  definition: section,
                },
              }
            : sections,
        {}
      );
      return acc;
    }, {})
);

function createCustomSectionName(widgetId: string, sectionTitle: string): string {
  return `${widgetId}-${sectionTitle.replace(/\s+/g, "-")}`;
}

export const getInstalledApplication = (state: IWithApplicationsState, id: string): IApplication => {
  const apps = installedAppsSelector(state);

  return apps[id];
};

export const isApplicationInstalled = (state: IWithApplicationsState, id: string): boolean =>
  isDefaultIntegration(id) || !!installedAppsSelector(state)[id];

export const getPrivateApplication = (state: IWithApplicationsState, id: string): IApplication => {
  const apps = privateAppsSelector(state);

  return apps[id];
};

export const isPrivateApplication = (state: IWithApplicationsState, id: string): boolean =>
  !!getPrivateApplication(state, id);

export const getInReviewApplication = (state: IWithApplicationsState, id: string): IApplication => {
  const apps = inReviewAppsSelector(state);

  return apps[id];
};

export const isInReviewApplication = (state: IWithApplicationsState, id: string): boolean =>
  !!getInReviewApplication(state, id);

export const getInstalledApplicationsCount = (state: IWithApplicationsState): number => {
  const apps = getFilteredInstalledApplications(state);
  const appsCount = Object.keys(apps)?.length || 0;
  const preinstalledAppsCount = 2; // LiveChat and Zapier

  return appsCount + preinstalledAppsCount;
};

export const getPrivateApplicationsCount = (state: IWithApplicationsState): number => {
  const apps = privateAppsSelector(state);

  return Object.keys(apps)?.length || 0;
};

export const getInReviewApplicationsCount = (state: IWithApplicationsState): number => {
  const apps = inReviewAppsSelector(state);

  return Object.keys(apps)?.length || 0;
};

export const getInstalledApplicationBySlug = (state: IWithApplicationsState, slug: string): IApplication => {
  const apps = getInstalledApplications(state) || [];

  // @ts-expect-error TS(2322): Type 'IApplication | undefined' is not assignable ... Remove this comment to see the full error message
  return apps.find(app => app.slug === slug);
};

// In terms of not published applications slug will be the same as application id
export const getNotPublishedApplication = (state: IWithApplicationsState, id: string): IApplication =>
  getPrivateApplication(state, id) || getInReviewApplication(state, id);

export const getApplicationBySlug = (state: IWithApplicationsState, slug: string): IApplication => {
  const publishedApp = getInstalledApplicationBySlug(state, slug);
  const notPublishedApp = getNotPublishedApplication(state, slug);

  return publishedApp || notPublishedApp;
};

export const getApplication = (state: IWithApplicationsState, id: string): IApplication => {
  const publishedApp = getInstalledApplication(state, id);
  const notPublishedApp = getNotPublishedApplication(state, id);

  return publishedApp || notPublishedApp;
};

export const getApplicationSettingsWidget = (state: IWithApplicationsState, id: string): IApplicationWidget => {
  const application = getApplication(state, id);

  if (!application) {
    // @ts-expect-error TS(2322): Type 'null' is not assignable to type 'IApplicatio... Remove this comment to see the full error message
    return null;
  }

  const widgets = Object.values(application.elements.widgets || {});
  // @ts-expect-error TS(2322): Type 'IApplicationWidget | undefined' is not assig... Remove this comment to see the full error message
  return widgets.find(({ placement }) => placement === "settings");
};

export const getIsFetchingApplications = createRequestFetchingSelector([
  ApplicationsActionNames.INSTALLED_APPS[RequestAction.REQUEST],
]);
export const getIsFetchingPrivateApplications = createRequestFetchingSelector([
  ApplicationsActionNames.PRIVATE_APPS[RequestAction.REQUEST],
]);
export const getIsFetchingInReviewApplications = createRequestFetchingSelector([
  ApplicationsActionNames.IN_REVIEW_APPS[RequestAction.REQUEST],
]);
export const hasFetchedApplications = createHasFetchedSelector([
  ApplicationsActionNames.INSTALLED_APPS[RequestAction.SUCCESS],
]);
export const hasFetchedPrivateApplications = createHasFetchedSelector([
  ApplicationsActionNames.PRIVATE_APPS[RequestAction.SUCCESS],
]);
export const hasFailedPrivateApplications = createRequestErrorSelector([
  ApplicationsActionNames.PRIVATE_APPS[RequestAction.FAILURE],
]);
export const hasFetchedInReviewApplications = createHasFetchedSelector([
  ApplicationsActionNames.IN_REVIEW_APPS[RequestAction.SUCCESS],
]);

export const hasFetchedAllApplicationLists = (state: IWithRequestsState): boolean => {
  const isInstalledAppsFetched = hasFetchedApplications(state);
  const isInReviewAppsFetched = hasFetchedInReviewApplications(state);

  // Private apps endpoint could return 401 for users who haven't created app
  const isPrivateAppsFetched = hasFetchedPrivateApplications(state) || !!hasFailedPrivateApplications(state);

  return isInstalledAppsFetched && isPrivateAppsFetched && isInReviewAppsFetched;
};

export const getAppDesignationUrl = (state: IWithApplicationsState, id: string): string => {
  const isInReviewApp = isInReviewApplication(state, id);
  const isPrivateApp = isPrivateApplication(state, id);

  if (isPrivateApp) {
    return ApplicationsDesignationUrl.Private;
  }

  if (isInReviewApp) {
    return ApplicationsDesignationUrl.InReview;
  }

  return ApplicationsDesignationUrl.Standard;
};

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const canManageApplications = (state: IWithAgentsSessionState) => {
  const isOwner = selectIsAdmin(state);

  return isOwner;
};
